/**
 * Data transformation utilities for converting between camelCase and snake_case
 * to maintain consistency between frontend and backend APIs
 */

/**
 * Convert camelCase object keys to snake_case
 * @param {Object} obj - Object with camelCase keys
 * @returns {Object} - Object with snake_case keys
 */
export const toSnakeCase = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }
  if (typeof obj !== 'object') return obj;

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    result[snakeKey] = toSnakeCase(value);
  }
  return result;
};

/**
 * Convert snake_case object keys to camelCase
 * @param {Object} obj - Object with snake_case keys
 * @returns {Object} - Object with camelCase keys
 */
export const toCamelCase = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }
  if (typeof obj !== 'object') return obj;

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = toCamelCase(value);
  }
  return result;
};

/**
 * Transform inventory item form data for API submission
 * @param {Object} formData - Form data in camelCase
 * @returns {Object} - API-ready data in snake_case
 */
export const transformInventoryItemForAPI = (formData) => {
  const apiData = {
    tenant_id: formData.tenantId,
    serial_number: formData.serialNumber,
    part_name: formData.partName,
    machine_id: formData.machineId || null,
    purchase_quantity: parseInt(formData.purchaseQuantity),
    rate_per_unit: parseFloat(formData.ratePerUnit),
    total_amount: parseFloat(formData.totalAmount) || null,
    po_item_id: formData.poItemId || null,
    remark: formData.remark || null,
    site: formData.site || null,
    delivery_status: formData.deliveryStatus || null
  };

  // Remove null values for optional fields
  Object.keys(apiData).forEach(key => {
    if (apiData[key] === null || apiData[key] === '') {
      delete apiData[key];
    }
  });

  return apiData;
};

/**
 * Transform inventory item API response to frontend format
 * @param {Object} apiData - API response data in snake_case
 * @returns {Object} - Frontend-ready data in camelCase
 */
export const transformInventoryItemFromAPI = (apiData) => {
  const transformed = toCamelCase(apiData);
  const createdAtVal = transformed.createdAt;
  let updatedAtVal = transformed.safeUpdatedAt || transformed.updatedAt || null;
  if (createdAtVal && updatedAtVal) {
    const c = new Date(createdAtVal).getTime();
    const u = new Date(updatedAtVal).getTime();
    if (!Number.isNaN(c) && !Number.isNaN(u) && u < c) {
      updatedAtVal = createdAtVal;
    }
  } else if (createdAtVal && !updatedAtVal) {
    updatedAtVal = createdAtVal;
  }
  const status = (() => {
    if (transformed.isActive === false) return 'Inactive';
    if (typeof transformed.currentQuantity === 'number' && typeof transformed.reorderLevel === 'number') {
      if (transformed.currentQuantity === 0) return 'Out of Stock';
      if (transformed.currentQuantity < transformed.reorderLevel) return 'Low Stock';
      return 'Active';
    }
    return transformed.isActive === false ? 'Inactive' : 'Active';
  })();

  return {
    ...transformed,
    // Map inventoryItemId to id for consistency
    id: transformed.inventoryItemId,
    // Normalize fields used by detail views
    sku: transformed.serialNumber || transformed.sku,
    name: transformed.partName || transformed.name,
    currentQuantity: typeof transformed.currentQuantity === 'number' ? transformed.currentQuantity : null,
    purchaseQuantity: typeof transformed.purchaseQuantity === 'number' ? transformed.purchaseQuantity : null,
    ratePerUnit: transformed.ratePerUnit != null ? Number(transformed.ratePerUnit) : null,
    totalAmount: transformed.totalAmount != null ? Number(transformed.totalAmount) : null,
    reorderLevel: typeof transformed.reorderLevel === 'number' ? transformed.reorderLevel : null,
    machineName: transformed.machineName || transformed.machine?.machineName,
    machineNumber: transformed.machineNumber || transformed.machine?.machineNumber,
    supplierName: transformed.supplierName || transformed.supplier?.name || 'N/A',
    description: transformed.remark || transformed.description,
    updatedAt: updatedAtVal,
    status
  };
};

/**
 * Transform inventory usage data for API submission
 * @param {Object} usageData - Usage form data
 * @returns {Object} - API-ready usage data
 */
export const transformInventoryUsageForAPI = (usageData) => {
  return {
    quantity: parseInt(usageData.quantity),
    to_machine_id: usageData.toMachineId || null,
    movement_date: usageData.movementDate || null,
    reason: usageData.reason,
    reference_number: usageData.referenceNumber || null
  };
};

/**
 * Transform purchase order form data for API submission
 * @param {Object} formData - Form data in camelCase
 * @returns {Object} - API-ready data structure
 */
export const transformPurchaseOrderForAPI = (formData) => {
  const contactPersonNumber = formData.contactPersonNumber
    ? formData.contactPersonNumber.replace(/[\s\-+()]/g, '')
    : '';

  const purchaseOrder = {
    po_number: formData.poNumber,
    po_date: formData.poDate,
    tenant_id: formData.tenantId,
    supplier_id: formData.supplierId,
    machine_id: formData.machineId && formData.machineId.trim() !== '' ? formData.machineId : null,
    contact_person_name: formData.contactPersonName || '',
    contact_person_number: contactPersonNumber,
    destination: formData.destination || '',
    terms_id: formData.termsId && formData.termsId.trim() !== '' ? formData.termsId : null,
    bank_id: formData.bankId && formData.bankId.trim() !== '' ? formData.bankId : null,
    currency: formData.currency || 'USD',
    status: formData.status || 'DRAFT'
  };

  const items = (formData.items || []).map((item, index) => {
    const transformed = {
      line_number: index + 1,
      description: item.description,
      specifications: item.specifications || '',
      container_details: item.containerDetails || ''
    };

    transformed.inventory_details = {
      serial_number: item.inventoryDetails?.serialNumber || '',
      site: item.inventoryDetails?.site || '',
      remark: item.inventoryDetails?.remark || null
    };

    // Include required_quantity if provided
    if (item.requiredQuantity != null && item.requiredQuantity !== '' && !isNaN(item.requiredQuantity)) {
      transformed.required_quantity = parseFloat(item.requiredQuantity);
    }

    // Only include pricing fields if they have actual values
    // (supplier fills these in later for DRAFT POs)
    if (item.unit) transformed.unit = item.unit;
    if (item.quantityPerUnit != null && item.quantityPerUnit !== '' && !isNaN(item.quantityPerUnit)) {
      transformed.quantity_per_unit = parseInt(item.quantityPerUnit);
    }
    if (item.unitPrice != null && item.unitPrice !== '' && !isNaN(item.unitPrice)) {
      transformed.unit_price = parseFloat(item.unitPrice);
    }
    if (item.totalPrice != null && item.totalPrice !== '' && !isNaN(item.totalPrice)) {
      transformed.total_price = parseFloat(item.totalPrice);
    }
    if (item.gstPercentage != null && item.gstPercentage !== '' && !isNaN(item.gstPercentage)) {
      transformed.gst_percentage = parseFloat(item.gstPercentage);
    }
    if (item.gstAmount != null && item.gstAmount !== '' && !isNaN(item.gstAmount)) {
      transformed.gst_amount = parseFloat(item.gstAmount);
    }

    return transformed;
  });

  // Only include calculations if there are actual values
  const hasCalculations = formData.calculations &&
    (formData.calculations.subTotal || formData.calculations.totalAmount);
  const calculations = hasCalculations ? {
    sub_total: parseFloat(formData.calculations?.subTotal || 0),
    round_off: parseFloat(formData.calculations?.roundOff || 0),
    gst_percentage: parseFloat(formData.calculations?.gstPercentage || 0),
    gst_amount: parseFloat(formData.calculations?.gstAmount || 0),
    total_amount: parseFloat(formData.calculations?.totalAmount || 0)
  } : null;

  return {
    purchase_order: purchaseOrder,
    items: items,
    ...(calculations ? { calculations } : {})
  };
};

/**
 * Transform purchase order API response to frontend format
 * @param {Object} apiData - API response data in snake_case
 * @returns {Object} - Frontend-ready data in camelCase
 */
export const transformPurchaseOrderFromAPI = (apiData) => {
  console.log('transformPurchaseOrderFromAPI - Original apiData:', apiData);
  const transformed = toCamelCase(apiData);
  console.log('transformPurchaseOrderFromAPI - After toCamelCase:', transformed);
  console.log('transformPurchaseOrderFromAPI - transformed.poId:', transformed.poId);

  // Add missing fields that frontend expects
  const result = {
    ...transformed,
    // Map po_id to id for consistency
    id: transformed.poId,
    // Use supplier_name from backend if available, otherwise fallback to placeholder
    supplier: transformed.supplierName || (transformed.supplierId ? `Supplier ${transformed.supplierId.slice(-4)}` : 'N/A'),
    supplierId: transformed.supplierId,
    // Use created_by_name or created_by_username from backend if available, otherwise fallback to placeholder
    createdBy: transformed.createdByName || transformed.createdByUsername || (transformed.createdBy ? `User ${transformed.createdBy.slice(-4)}` : 'N/A'),
    approvedByUsername: transformed.approvedByUsername || null,
    approvedById: transformed.approvedById || null,
    approvedBy: transformed.approvedByName || transformed.approvedByUsername || transformed.approvedById || '',
    rejectedByUsername: transformed.rejectedByUsername || null,
    rejectedById: transformed.rejectedById || null,
    rejectedBy: transformed.rejectedByName || transformed.rejectedByUsername || transformed.rejectedById || '',
    updatedByName: transformed.updatedByName || null,
    updatedByUsername: transformed.updatedByUsername || null,
    // Use total_amount from backend if available, otherwise default to 0
    total: transformed.totalAmount || 0,
    // Use machine_name from backend if available, otherwise fallback to placeholder
    machine: transformed.machineName || (transformed.machineId ? `Machine ${transformed.machineId.slice(-4)}` : 'N/A'),
    // Delivery info provided by supplier
    supplierDeliveryDate: transformed.supplierDeliveryDate || null,
    supplierRemarks: transformed.supplierRemarks || null,
    // Add placeholder for location (not in schema)
    location: 'Warehouse A' // Default location
  };

  console.log('transformPurchaseOrderFromAPI - Final result:', result);
  console.log('transformPurchaseOrderFromAPI - Final result.id:', result.id);

  return result;
};

/**
 * Transform document data for API submission
 * @param {Object} documentData - Document form data
 * @returns {Object} - API-ready document data
 */
export const transformDocumentForAPI = (documentData) => {
  return {
    owner_type: documentData.ownerType?.toUpperCase() || documentData.ownerType, // Ensure uppercase for enum
    owner_id: documentData.ownerId,
    document_type: documentData.documentType,
    document_number: documentData.documentName || documentData.documentNumber,
    file_path: documentData.filePath,
    remark: documentData.remark || null
  };
};

/**
 * Transform document data from API response
 * @param {Object} apiData - API response data
 * @returns {Object} - Frontend-ready document data
 */
export const transformDocumentFromAPI = (apiData) => {
  return {
    documentId: apiData.document_id,
    ownerType: apiData.owner_type,
    ownerId: apiData.owner_id,
    documentType: apiData.document_type,
    documentNumber: apiData.document_number, // Map document_number to documentNumber
    documentName: apiData.document_number, // Map document_number to documentName for backward compatibility
    filePath: apiData.file_path,
    issueDate: apiData.issue_date,
    expiryDate: apiData.expiry_date,
    isVerified: apiData.is_verified,
    verifiedBy: apiData.verified_by,
    verifiedAt: apiData.verified_at,
    isActive: apiData.is_active,
    remark: apiData.remark,
    createdBy: apiData.created_by,
    updatedBy: apiData.updated_by,
    deletedBy: apiData.deleted_by,
    createdAt: apiData.created_at,
    updatedAt: apiData.updated_at,
    deletedAt: apiData.deleted_at,
    // Add placeholder values for missing fields
    fileSize: null, // Not available in current schema
    mimeType: null // Not available in current schema
  };
};

/**
 * Transform bank details data for API submission
 * @param {Object} bankData - Bank details form data
 * @param {boolean} isUpdate - Whether this is for an update operation (default: false)
 * @returns {Object} - API-ready bank details data
 */
export const transformBankDetailsForAPI = (bankData, isUpdate = false) => {
  const transformed = {
    bank_name: bankData.bankName,
    account_number: bankData.accountNumber,
    ifsc_code: bankData.ifscCode,
    branch_name: bankData.branchName || null,
    account_holder_name: bankData.accountHolderName || null
  };

  // For create operations, include owner_type and owner_id
  // For update operations, only send the 5 allowed fields (owner information cannot be changed)
  if (!isUpdate) {
    transformed.owner_type = bankData.ownerType?.toUpperCase() || bankData.ownerType;
    transformed.owner_id = bankData.ownerId;
  }

  return transformed;
};

/**
 * Transform terms and conditions data for API submission
 * @param {Object} termsData - Terms form data
 * @returns {Object} - API-ready terms data
 */
export const transformTermsForAPI = (termsData) => {
  const apiData = {
    tenant_id: termsData.tenantId,
    supplier_id: termsData.supplierId,
    payment_terms_number: termsData.paymentTermsNumber || null,
    payment_terms_description: termsData.paymentTermsDescription || null,
    delivery_terms: termsData.deliveryTerms || null,
    material_delivery: termsData.materialDelivery || null,
    effective_from: termsData.effectiveFrom || null,
    effective_to: termsData.effectiveTo || null,
    is_active: termsData.isActive !== false,
    remark: termsData.remark || null
  };

  // Convert null values to empty strings for optional string fields to avoid validation errors
  // Keep null for date fields and other non-string fields
  const stringFields = ['payment_terms_number', 'payment_terms_description', 'delivery_terms', 'material_delivery', 'remark'];
  stringFields.forEach(field => {
    if (apiData[field] === null) {
      apiData[field] = '';
    }
  });

  return apiData;
};

/**
 * Transform API response to frontend format for any entity
 * @param {Object|Array} apiData - API response data
 * @returns {Object|Array} - Frontend-ready data
 */
export const transformFromAPI = (apiData) => {
  return toCamelCase(apiData);
};

/**
 * Transform frontend data to API format for any entity
 * @param {Object|Array} frontendData - Frontend data
 * @returns {Object|Array} - API-ready data
 */
export const transformToAPI = (frontendData) => {
  return toSnakeCase(frontendData);
};
